import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Container,
} from "reactstrap";
import Section from './Section';
import def from "ajv/dist/vocabularies/applicator/additionalItems";
import { createStock } from "../../api/stock/addStockApi";

const validationSchema = Yup.object({
  Product_Name: Yup.string().required("Product Name is required"),
  Description: Yup.string(),
  Boat_Owner_Number: Yup.string(),
  Boat_Number: Yup.string(),
  Harbour: Yup.string(),
  Scientific_Name: Yup.string().required("Scientific Name is required"),
  Catch_Location: Yup.string().required("Catch Location is required"),
  Fishing_Method: Yup.string().required("Fishing Method is required"),
  Date_of_Catch: Yup.date().required("Date of Catch is required").typeError("Invalid date"),
  Processing_Type: Yup.string().required("Processing Type is required"),
  Size_Weight: Yup.string().required("Size/Weight is required"),
  Quality_Grade: Yup.string().required("Quality Grade is required"),
  Price_per_Kg: Yup.number().positive("Must be a positive number").required("Price per Kg is required"),
  Minimum_Order_Quantity: Yup.number().positive("Must be a positive number").required("Minimum Order Quantity is required"),
  Total_Available_Quantity: Yup.number().positive("Must be a positive number").required("Total Available Quantity is required"),
  Sustainability_Certification: Yup.string(),
  Shipping: Yup.string(),
  Shipping_Fee: Yup.number().positive("Must be a positive number"),
  Sold: Yup.boolean(),
});

const StyledProductForm = () => {
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      Product_Name: "",
      Description: "",
      Boat_Owner_Number: "",
      Boat_Number: "",
      Harbour: "",
      Scientific_Name: "",
      Catch_Location: "",
      Fishing_Method: "",
      Date_of_Catch: "",
      Processing_Type: "",
      Size_Weight: "",
      Quality_Grade: "",
      Price_per_Kg: "",
      Minimum_Order_Quantity: "",
      Total_Available_Quantity: "",
      Sustainability_Certification: "",
      Shipping: "",
      Shipping_Fee: "",
      Sold: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const response = await createStock(values);
        console.log("Form submitted successfully:", response.data);
      } catch (error) {
        console.error("Error submitting form:", error);
      }

    },
  });

  return (
    <React.Fragment>
      <Container className="py-4">
        <Form onSubmit={formik.handleSubmit} className="p-4 border rounded shadow-sm">
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="Product_Name">Product Name</Label>
                <Input
                  id="Product_Name"
                  name="Product_Name"
                  type="select" // Dropdown
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Product_Name}
                  invalid={!!(formik.touched.Product_Name && formik.errors.Product_Name)}
                >
                  <option value="">Select a Fish</option> {/* Default placeholder option */}
                  {[
                    "Yellowfin Tuna",
                    "Skipjack Tuna",
                    "Mahi-Mahi (Dolphinfish)",
                    "Indo-Pacific Sailfish",
                    "Blue Marlin",
                    "Bigeye Tuna",
                    "Spanish Mackerel (Seer Fish)",
                    "Barracuda",
                    "Indian Oil Sardine",
                    "Silver Pomfret",
                    "Blacktip Reef Shark",
                    "Grouper",
                    "Snapper",
                    "Emperor Fish",
                    "Parrotfish",
                    "Milkfish",
                    "Spotted Scat",
                    "Spiny Lobster",
                    "Mud Crab",
                    "Pearlspot (Etroplus)",
                  ].map((fish) => (
                    <option key={fish} value={fish}>
                      {fish}
                    </option>
                  ))}
                </Input>
                {formik.touched.Product_Name && formik.errors.Product_Name && (
                  <div className="text-danger small">{formik.errors.Product_Name}</div>
                )}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="Description">Description</Label>
                <Input
                  id="Description"
                  name="Description"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Description}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Boat_Owner_Number">Boat Owner Number</Label>
                <Input
                  id="Boat_Owner_Number"
                  name="Boat_Owner_Number"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Boat_Owner_Number}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Boat_Number">Boat Number</Label>
                <Input
                  id="Boat_Number"
                  name="Boat_Number"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Boat_Number}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Harbour">Harbour</Label>
                <Input
                  id="Harbour"
                  name="Harbour"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Harbour}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Scientific_Name">Scientific Name</Label>
                <Input
                  id="Scientific_Name"
                  name="Scientific_Name"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Scientific_Name}
                  invalid={!!(formik.touched.Scientific_Name && formik.errors.Scientific_Name)}
                />
                {formik.touched.Scientific_Name && formik.errors.Scientific_Name && (
                  <div className="text-danger small">{formik.errors.Scientific_Name}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Catch_Location">Catch Location</Label>
                <Input
                  id="Catch_Location"
                  name="Catch_Location"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Catch_Location}
                  invalid={!!(formik.touched.Catch_Location && formik.errors.Catch_Location)}
                />
                {formik.touched.Catch_Location && formik.errors.Catch_Location && (
                  <div className="text-danger small">{formik.errors.Catch_Location}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Fishing_Method">Fishing Method</Label>
                <Input
                  id="Fishing_Method"
                  name="Fishing_Method"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Fishing_Method}
                  invalid={!!(formik.touched.Fishing_Method && formik.errors.Fishing_Method)}
                />
                {formik.touched.Fishing_Method && formik.errors.Fishing_Method && (
                  <div className="text-danger small">{formik.errors.Fishing_Method}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Date_of_Catch">Date of Catch</Label>
                <Input
                  id="Date_of_Catch"
                  name="Date_of_Catch"
                  type="date"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Date_of_Catch}
                  invalid={!!(formik.touched.Date_of_Catch && formik.errors.Date_of_Catch)}
                />
                {formik.touched.Date_of_Catch && formik.errors.Date_of_Catch && (
                  <div className="text-danger small">{formik.errors.Date_of_Catch}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Processing_Type">Processing Type</Label>
                <Input
                  id="Processing_Type"
                  name="Processing_Type"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Processing_Type}
                  invalid={!!(formik.touched.Processing_Type && formik.errors.Processing_Type)}
                />
                {formik.touched.Processing_Type && formik.errors.Processing_Type && (
                  <div className="text-danger small">{formik.errors.Processing_Type}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Size_Weight">Size/Weight</Label>
                <Input
                  id="Size_Weight"
                  name="Size_Weight"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Size_Weight}
                  invalid={!!(formik.touched.Size_Weight && formik.errors.Size_Weight)}
                />
                {formik.touched.Size_Weight && formik.errors.Size_Weight && (
                  <div className="text-danger small">{formik.errors.Size_Weight}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Quality_Grade">Quality Grade</Label>
                <Input
                  id="Quality_Grade"
                  name="Quality_Grade"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Quality_Grade}
                  invalid={!!(formik.touched.Quality_Grade && formik.errors.Quality_Grade)}
                />
                {formik.touched.Quality_Grade && formik.errors.Quality_Grade && (
                  <div className="text-danger small">{formik.errors.Quality_Grade}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Price per Kg">Price per Kg</Label>
                <Input
                  id="Price_per_Kg"
                  name="Price_per_Kg"
                  type="Number"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Price_per_Kge}
                  invalid={!!(formik.touched.Price_per_Kg && formik.errors.Price_per_Kg)}
                />
                {formik.touched.Price_per_Kg && formik.errors.Price_per_Kg && (
                  <div className="text-danger small">{formik.errors.Price_per_Kg}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Minimum Order Quantity">Minimum Order Quantity</Label>
                <Input
                  id="Minimum_Order_Quantity"
                  name="Minimum_Order_Quantity"
                  type="Number"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Minimum_Order_Quantity}
                  invalid={!!(formik.touched.Minimum_Order_Quantity && formik.errors.Quality_Grade)}
                />
                {formik.touched.Minimum_Order_Quantity && formik.errors.Minimum_Order_Quantity && (
                  <div className="text-danger small">{formik.errors.Minimum_Order_Quantity}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Total_Available_Quantity">Total Available Quantity</Label>
                <Input
                  id="Total_Available_Quantity"
                  name="Total_Available_Quantity"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Total_Available_Quantity}
                  invalid={!!(formik.touched.Total_Available_Quantity && formik.errors.Total_Available_Quantity)}
                />
                {formik.touched.Total_Available_Quantity && formik.errors.Total_Available_Quantity && (
                  <div className="text-danger small">{formik.errors.Total_Available_Quantity}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Sustainability_Certification">Sustainability Certification</Label>
                <Input
                  id="Sustainability_Certification"
                  name="Sustainability_Certification"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Sustainability_Certification}
                  invalid={!!(formik.touched.Sustainability_Certification && formik.errors.Sustainability_Certification)}
                />
                {formik.touched.Sustainability_Certification && formik.errors.Sustainability_Certification && (
                  <div className="text-danger small">{formik.errors.Sustainability_Certification}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Shipping">Shipping</Label>
                <Input
                  id="Shipping"
                  name="Shipping"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Shipping}
                  invalid={!!(formik.touched.Shipping && formik.errors.Shipping)}
                />
                {formik.touched.Shipping && formik.errors.Shipping && (
                  <div className="text-danger small">{formik.errors.Shipping}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Shipping_Fee">Shipping Fee</Label>
                <Input
                  id="Shipping_Fee"
                  name="Shipping_Fee"
                  type="text"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Shipping_Fee}
                  invalid={!!(formik.touched.Shipping_Fee && formik.errors.Shipping_Fee)}
                />
                {formik.touched.Shipping_Fee && formik.errors.Shipping_Fee && (
                  <div className="text-danger small">{formik.errors.Shipping_Fee}</div>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="Sold">Sold</Label>
                <Input
                  id="Sold"
                  name="Sold"
                  type="checkbox"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.Sold}
                  invalid={!!(formik.touched.Sold && formik.errors.Sold)}
                  style={{ marginLeft: 10 }}
                />
                {formik.touched.Sold && formik.errors.Sold && (
                  <div className="text-danger small">{formik.errors.Sold}</div>
                )}
              </FormGroup>
            </Col>

            <div className="text-end">
              <Button onClick={() => navigate("/plantOwnerDashboard")} style={{ marginRight: "1%" }}>Back</Button>
              <Button type="submit">Add Stock</Button>
            </div>

          </Row>

        </Form>
      </Container>
    </React.Fragment>);
}
export default StyledProductForm